// The categorical tag ramp (#11426).
//
// Tag colour means CATEGORY, and the hue is *derived from the value* rather
// than assigned per surface. That is what makes "a category keeps its colour
// across pages" structural: nothing stores a value-to-hue mapping, so
// "Capital call" is the same orange on a fund, on a manager and in the record
// browser without anyone maintaining a table.
//
// Ported from the design spec's `collection.js` hueFor.

// Red, orange and pink are absent by design, not by omission (register rule
// 23). A hue that carries a verdict before anything assigns it one cannot
// mean "category", and form cannot rescue it the way form rescued the other
// four: a filled red chip still shouts, and the judgement scale never claimed
// red, which is precisely why the collision argument missed it. Keeping them
// out of the type as well as the ramp makes the mistake unrepresentable
// rather than merely unlikely.
export type TagHue = "blue" | "teal" | "green" | "amber" | "purple" | "slate";

// Six is the ceiling, not a first cut. With the three valence hues removed,
// this palette's muted register does not hold more that are mutually distinct
// AT CHIP SIZE — pushing further means near-neighbours, which is the
// resolution problem rule 16 warns about arriving from the other side.
//
// Blue, green, amber and slate sit here despite matching the judgement scale
// hex-for-hex. They only ever collided while hue was the sole cue; judgement
// is now ink-plus-a-dot and category owns the fill, so form carries the
// channel and a filled blue chip cannot be read as eval-blue.
export const TAG_HUES: readonly TagHue[] = [
  "blue",
  "teal",
  "green",
  "amber",
  "purple",
  "slate",
];

// The handful of values that already carry a colour convention in the product.
// Everything else derives, because a pinned table that grows is a table someone
// has to keep.
const PINNED: Record<string, TagHue> = {
  Meeting: "purple",
  Call: "blue",
  Email: "amber",
  // Was pink, which rule 23 retired from the ramp entirely.
  Note: "teal",
};

/** The hue a categorical value renders in, everywhere it appears. */
export function hueFor(value: string): TagHue {
  const pinned = PINNED[value];
  if (pinned) return pinned;

  let hash = 0;
  for (const char of String(value)) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return TAG_HUES[hash % TAG_HUES.length];
}
